import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Stack,
  Switch,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  SupportAgent,
  Mosque,
  PhoneAndroid,
  Email,
  Person,
  LocationCity,
  CheckCircle,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { resolveProductLine, isUmrahLine } from '../../../utils/productLineConfig';
import {
  umrahTransportOptions,
  leisureTransportOptions,
  ziaraatOptions,
  carRentalClasses,
} from './transportOptions';

const TransportOptionCard = ({ opt, selected, onSelect }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={() => onSelect(opt.value)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') onSelect(opt.value);
    }}
    sx={{
      p: 2,
      borderRadius: 2,
      cursor: 'pointer',
      border: '2px solid',
      borderColor: selected ? 'secondary.main' : 'divider',
      bgcolor: selected ? 'rgba(31, 111, 120, 0.07)' : 'background.paper',
      boxShadow: selected ? '0 8px 20px rgba(31, 111, 120, 0.12)' : 'none',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
      height: '100%',
      '&:hover': {
        borderColor: selected ? 'secondary.main' : 'secondary.light',
        boxShadow: '0 4px 14px rgba(14, 28, 42, 0.08)',
      },
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: selected ? 'secondary.main' : 'action.hover',
          color: selected ? 'common.white' : 'text.secondary',
        }}
      >
        {opt.icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {opt.label}
          </Typography>
          {selected && <CheckCircle color="secondary" sx={{ fontSize: 18 }} />}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {opt.detail}
        </Typography>
      </Box>
    </Stack>
  </Box>
);

const ZiaraatOptionCard = ({ opt, selected, onSelect }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={() => onSelect(opt.value)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') onSelect(opt.value);
    }}
    sx={{
      p: 2,
      borderRadius: 2,
      cursor: 'pointer',
      border: '2px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: selected ? 'rgba(14, 28, 42, 0.04)' : 'background.paper',
      height: '100%',
      '&:hover': { borderColor: 'primary.light' },
    }}
  >
    <Typography variant="subtitle1" fontWeight={700}>{opt.label}</Typography>
    <Typography variant="body2" color="text.secondary">{opt.city}</Typography>
  </Box>
);

const TransportationContactStepV2 = ({ bookingData, updateBookingData }) => {
  const productLine = resolveProductLine(bookingData);
  const isUmrah = isUmrahLine(bookingData);
  const showTransport = bookingData.includeTransport !== undefined ? !!bookingData.includeTransport : true;
  const showZiaraat =
    isUmrah && (bookingData.includeZiaraat !== undefined ? !!bookingData.includeZiaraat : true);
  const showHomePickup = showTransport && !!bookingData.includeHomePickup;
  const transportOptions = isUmrah ? umrahTransportOptions : leisureTransportOptions;
  const handleSelect = (key, value) => updateBookingData({ [key]: value });
  const handleInput = (key) => (e) => updateBookingData({ [key]: e.target.value });

  const isSelfDrive = bookingData.transportationType === 'Self Drive';

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SupportAgent color="primary" />
        {showTransport || showZiaraat ? 'Transportation & Contact' : 'Contact Details'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {productLine} trip — choose how you move between cities, then add your contact details.
      </Typography>

      <Grid container spacing={3}>
        {showTransport && (
          <Grid item xs={12} md={showZiaraat ? 6 : 12}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    Select transportation
                  </Typography>
                  {showHomePickup && (
                    <Typography variant="body2" color="secondary.main" fontWeight={600} sx={{ mb: 2 }}>
                      Home pickup included — our agent will confirm your address.
                    </Typography>
                  )}
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    {transportOptions.map((opt) => (
                      <Grid item xs={12} sm={showZiaraat ? 12 : 4} key={opt.value}>
                        <TransportOptionCard
                          opt={opt}
                          selected={bookingData.transportationType === opt.value}
                          onSelect={(value) => {
                            const patch = { transportationType: value };
                            if (value === 'Self Drive') {
                              patch.includeCarRental = true;
                              if (!bookingData.carRentalClass) patch.carRentalClass = 'Sedan';
                            }
                            updateBookingData(patch);
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  {isSelfDrive && !isUmrah && (
                    <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                        Vehicle class
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        fullWidth
                        size="small"
                        value={bookingData.carRentalClass || 'Sedan'}
                        onChange={(_, val) => val && updateBookingData({ carRentalClass: val, includeCarRental: true })}
                      >
                        {carRentalClasses.map((c) => (
                          <ToggleButton key={c} value={c} sx={{ fontWeight: 600, textTransform: 'none' }}>
                            {c}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Sedan is our most popular self-drive option for city and highway trips.
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>24/7 online support</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Chat with our team while your trip is being confirmed
                      </Typography>
                    </Box>
                    <Switch
                      checked={!!bookingData.onlineSupport}
                      onChange={() => handleSelect('onlineSupport', !bookingData.onlineSupport)}
                      color="secondary"
                    />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        )}

        {showZiaraat && (
          <Grid item xs={12} md={showTransport ? 6 : 12}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Mosque color="primary" />
                    Ziaraat preference
                  </Typography>
                  <Stack spacing={1.5}>
                    {ziaraatOptions.map((opt) => (
                      <ZiaraatOptionCard
                        key={opt.value}
                        opt={opt}
                        selected={bookingData.makkahZiaraat === opt.value}
                        onSelect={(value) => {
                          updateBookingData({ makkahZiaraat: value, madinaZiaraat: value });
                        }}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        )}

        {!showTransport && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>24/7 online support</Typography>
                  <Typography variant="body2" color="text.secondary">Optional add-on for your quote</Typography>
                </Box>
                <Switch
                  checked={!!bookingData.onlineSupport}
                  onChange={() => handleSelect('onlineSupport', !bookingData.onlineSupport)}
                  color="secondary"
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                  Primary contact
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={bookingData.fullName}
                      onChange={handleInput('fullName')}
                      InputProps={{ startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} /> }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={bookingData.phoneNumber}
                      onChange={handleInput('phoneNumber')}
                      InputProps={{ startAdornment: <PhoneAndroid sx={{ mr: 1, color: 'action.active' }} /> }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="City"
                      value={bookingData.cityName}
                      onChange={handleInput('cityName')}
                      InputProps={{ startAdornment: <LocationCity sx={{ mr: 1, color: 'action.active' }} /> }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Email (optional)"
                      value={bookingData.emailAddress}
                      onChange={handleInput('emailAddress')}
                      InputProps={{ startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} /> }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Comments / Special Requests"
                      value={bookingData.comments}
                      onChange={handleInput('comments')}
                      multiline
                      minRows={3}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TransportationContactStepV2;
